import { useState, useMemo } from 'react';
import { useQuery, useMutation } from '@apollo/client/react';
import { gql } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { Navigation } from '../components/Navigation';
import { Breadcrumb } from '../components/Breadcrumb';
import { BulkActions } from '../components/admin/AdminActions';
import { CaseManagementActions } from '../components/admin/CaseManagementActions';
import { EditCaseModal } from '../components/admin/EditCaseModal';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { format } from 'date-fns';
import { canPerformAction } from '../utils/permissions';
import { CASE_TYPES } from '../constants/caseTypes';

const GET_CURRENT_USER = gql`
  query GetCurrentUser {
    getCurrentUser {
      id
      email
      firstName
      lastName
      role
    }
  }
`;

const LIST_CASES = gql`
  query ListCases($status: CaseStatus, $limit: Int, $nextToken: String) {
    listCases(status: $status, limit: $limit, nextToken: $nextToken) {
      items {
        id
        caseNumber
        status
        caseType
        description
        incidentDate
        assignedAttorneyId
        assignedCaseManagerId
        clientId
        client {
          id
          firstName
          lastName
          email
        }
        createdAt
        updatedAt
      }
      nextToken
    }
  }
`;

const LIST_STAFF = gql`
  query ListStaff {
    listStaff {
      id
      firstName
      lastName
      role
    }
  }
`;

const ASSIGN_CASE = gql`
  mutation AssignCase($id: ID!, $attorneyId: ID, $caseManagerId: ID) {
    assignCase(id: $id, attorneyId: $attorneyId, caseManagerId: $caseManagerId) {
      id
      assignedAttorneyId
      assignedCaseManagerId
    }
  }
`;

type SortField = 'caseNumber' | 'client' | 'caseType' | 'status' | 'createdAt' | 'updatedAt';
type SortDirection = 'asc' | 'desc';
type ViewDensity = 'compact' | 'comfortable' | 'spacious';

export function CasesList() {
  const navigate = useNavigate();
  
  // Filters
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [caseTypeFilter, setCaseTypeFilter] = useState<string>('');
  const [attorneyFilter, setAttorneyFilter] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showOnlyAssigned, setShowOnlyAssigned] = useState(false);
  
  // Table controls
  const [sortField, setSortField] = useState<SortField>('createdAt');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [viewDensity, setViewDensity] = useState<ViewDensity>('comfortable');
  const [selectedCases, setSelectedCases] = useState<Set<string>>(new Set());
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  
  // Bulk actions
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [bulkAttorney, setBulkAttorney] = useState('');

  // Edit modal
  const [editingCase, setEditingCase] = useState<any>(null);

  // Admin functions
  const handleEditCase = (caseItem: any) => {
    setEditingCase(caseItem);
  };

  const handleDeleteCase = async (caseId: string) => {
    if (!canPerformAction(user?.role, 'cases', 'delete')) {
      alert('You do not have permission to delete cases.');
      return;
    }
    
    try {
      // TODO: Implement delete case mutation
      console.log('Deleting case:', caseId);
      // await deleteCase({ variables: { id: caseId } });
      // refetch();
      alert('Case deleted successfully');
    } catch (error) {
      console.error('Error deleting case:', error);
      alert('Error deleting case');
    }
  };

  const handleBulkDelete = async () => {
    if (!canPerformAction(user?.role, 'cases', 'delete')) {
      alert('You do not have permission to delete cases.');
      return;
    }
    
    try {
      // TODO: Implement bulk delete mutation
      console.log('Bulk deleting cases:', Array.from(selectedCases));
      setSelectedCases(new Set());
      alert(`${selectedCases.size} cases deleted successfully`);
    } catch (error) {
      console.error('Error bulk deleting cases:', error);
      alert('Error deleting cases');
    }
  };

  const handleBulkExport = () => {
    const selectedData = cases.filter((c: any) => selectedCases.has(c.id));
    const csvContent = [
      ['Case Number', 'Client', 'Type', 'Status', 'Attorney', 'Created Date'].join(','),
      ...selectedData.map((c: any) => [
        c.caseNumber,
        c.client ? `"${c.client.firstName} ${c.client.lastName}"` : 'No Client',
        c.caseType,
        c.status,
        c.assignedAttorney ? `"${c.assignedAttorney.firstName} ${c.assignedAttorney.lastName}"` : 'Unassigned',
        format(new Date(c.createdAt), 'yyyy-MM-dd')
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `selected-cases-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const { data: userData, loading: userLoading } = useQuery<{ getCurrentUser: { id: string; email: string; firstName: string; lastName: string; role: string } }>(GET_CURRENT_USER);
  const { data, loading, error, refetch } = useQuery<{ listCases: { items: any[]; nextToken?: string } }>(LIST_CASES, {
    variables: { limit: 100 },
    fetchPolicy: 'network-only',
  });
  const { data: staffData } = useQuery<{ listStaff: any[] }>(LIST_STAFF);
  
  const [assignCase] = useMutation<{ assignCase: any }>(ASSIGN_CASE);

  const cases = data?.listCases?.items || [];
  const staffMembers = staffData?.listStaff || [];
  const user = userData?.getCurrentUser;
  
  const attorneys = staffMembers.filter((s: any) => s.role === 'ATTORNEY');
  const caseTypes = useMemo(() => {
    // Combine predefined types with any custom types from existing cases
    const existingTypes = new Set<string>(cases.map((c: any) => c.caseType));
    const allTypes = new Set([...CASE_TYPES, ...existingTypes]);
    return Array.from(allTypes).sort();
  }, [cases]);

  // Filtering, sorting, and pagination
  const { paginatedCases, totalPages, totalFiltered } = useMemo(() => {
    // First, exclude INQUIRY placeholder cases (they show in Inquiries)
    let filtered = cases.filter((c: any) => c.status !== 'INQUIRY').filter((c: any) => {
      // Search
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesSearch = 
          c.caseNumber.toLowerCase().includes(query) ||
          c.caseType.toLowerCase().includes(query) ||
          (c.client && `${c.client.firstName} ${c.client.lastName}`.toLowerCase().includes(query));
        if (!matchesSearch) return false;
      }
      
      // Status filter
      if (statusFilter && c.status !== statusFilter) return false;
      
      // Case type filter
      if (caseTypeFilter && c.caseType !== caseTypeFilter) return false;
      
      // Attorney filter
      if (attorneyFilter && c.assignedAttorneyId !== attorneyFilter) return false;
      
      // Assignment filter
      if (showOnlyAssigned && user) {
        const isAssigned = 
          c.assignedAttorneyId === user.id ||
          c.assignedCaseManagerId === user.id ||
          user.role === 'ADMIN';
        if (!isAssigned) return false;
      }
      
      return true;
    });

    // Sort
    filtered.sort((a: any, b: any) => {
      let aVal, bVal;
      
      switch (sortField) {
        case 'caseNumber':
          aVal = a.caseNumber;
          bVal = b.caseNumber;
          break;
        case 'client':
          aVal = a.client ? `${a.client.firstName} ${a.client.lastName}` : '';
          bVal = b.client ? `${b.client.firstName} ${b.client.lastName}` : '';
          break;
        case 'caseType':
          aVal = a.caseType;
          bVal = b.caseType;
          break;
        case 'status':
          aVal = a.status;
          bVal = b.status;
          break;
        case 'createdAt':
          aVal = new Date(a.createdAt).getTime();
          bVal = new Date(b.createdAt).getTime();
          break;
        case 'updatedAt':
          aVal = new Date(a.updatedAt).getTime();
          bVal = new Date(b.updatedAt).getTime();
          break;
        default:
          return 0;
      }
      
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    // Pagination
    const totalFiltered = filtered.length;
    const totalPages = Math.ceil(totalFiltered / pageSize);
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const paginatedCases = filtered.slice(startIndex, endIndex);

    return { paginatedCases, totalPages, totalFiltered };
  }, [cases, searchQuery, statusFilter, caseTypeFilter, attorneyFilter, showOnlyAssigned, user, sortField, sortDirection, currentPage, pageSize]);

  // Reset to page 1 when filters change
  const resetPagination = () => setCurrentPage(1);
  
  // Update filters to reset pagination
  const handleFilterChange = (setter: any) => (value: any) => {
    setter(value);
    resetPagination();
  };

  const getStaffName = (staffId: string) => {
    if (!staffId) return 'Unassigned';
    const staff = staffMembers.find((s: any) => s.id === staffId);
    return staff ? `${staff.firstName} ${staff.lastName}` : 'Unassigned';
  };

  const getStatusBadgeVariant = (status: string): 'default' | 'secondary' | 'success' | 'warning' | 'info' => {
    const variants: Record<string, any> = {
      NEW: 'info',
      IN_PROGRESS: 'warning',
      NEGOTIATION: 'warning',
      LITIGATION: 'warning',
      SETTLED: 'success',
      CLOSED: 'default',
    };
    return variants[status] || 'default';
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedCases(new Set(paginatedCases.map((c: any) => c.id)));
    } else {
      setSelectedCases(new Set());
    }
  };

  const handleSelectCase = (caseId: string, checked: boolean) => {
    const newSelected = new Set(selectedCases);
    if (checked) {
      newSelected.add(caseId);
    } else {
      newSelected.delete(caseId);
    }
    setSelectedCases(newSelected);
  };

  const handleBulkAssign = async () => {
    if (!bulkAttorney || selectedCases.size === 0) return;
    
    try {
      await Promise.all(
        Array.from(selectedCases).map(caseId =>
          assignCase({ variables: { id: caseId, attorneyId: bulkAttorney } })
        )
      );
      setSelectedCases(new Set());
      setShowBulkActions(false);
      setBulkAttorney('');
      refetch();
    } catch (err) {
      console.error('Error bulk assigning:', err);
      alert('Error assigning cases');
    }
  };

  const handleExport = () => {
    // Export ALL filtered cases, not just current page (excluding INQUIRY placeholder cases)
    const allFilteredCases = cases.filter((c: any) => c.status !== 'INQUIRY').filter((c: any) => {
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesSearch = 
          c.caseNumber.toLowerCase().includes(query) ||
          c.caseType.toLowerCase().includes(query) ||
          (c.client && `${c.client.firstName} ${c.client.lastName}`.toLowerCase().includes(query));
        if (!matchesSearch) return false;
      }
      if (statusFilter && c.status !== statusFilter) return false;
      if (caseTypeFilter && c.caseType !== caseTypeFilter) return false;
      if (attorneyFilter && c.assignedAttorneyId !== attorneyFilter) return false;
      if (showOnlyAssigned && user) {
        const isAssigned = 
          c.assignedAttorneyId === user.id ||
          c.assignedCaseManagerId === user.id ||
          user.role === 'ADMIN';
        if (!isAssigned) return false;
      }
      return true;
    });

    const csvContent = [
      ['Case Number', 'Client', 'Type', 'Status', 'Attorney', 'Created', 'Updated'].join(','),
      ...allFilteredCases.map((c: any) => [
        c.caseNumber,
        c.client ? `${c.client.firstName} ${c.client.lastName}` : '',
        c.caseType,
        c.status,
        getStaffName(c.assignedAttorneyId),
        format(new Date(c.createdAt), 'yyyy-MM-dd'),
        format(new Date(c.updatedAt), 'yyyy-MM-dd'),
      ].join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `cases-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
  };

  const clearFilters = () => {
    setSearchQuery('');
    setStatusFilter('');
    setCaseTypeFilter('');
    setAttorneyFilter('');
    setShowOnlyAssigned(false);
    setCurrentPage(1);
  };

  const goToPage = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const getPageNumbers = (): (number | string)[] => {
    const pages: (number | string)[] = [];
    const maxVisible = 7;
    
    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 4) {
        for (let i = 1; i <= 5; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 3) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 4; i <= totalPages; i++) pages.push(i);
      } else {
        pages.push(1);
        pages.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      }
    }
    
    return pages;
  };

  const densityClasses = {
    compact: 'py-2',
    comfortable: 'py-3',
    spacious: 'py-4',
  };

  if (userLoading || (loading && !data)) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-aalaw-red"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <h2 className="text-xl font-bold text-red-600">Error loading cases</h2>
          <p className="text-gray-600 mt-2">{error.message}</p>
          <Button onClick={() => navigate('/dashboard')} className="mt-4">
            Back to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) {
      return (
        <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
        </svg>
      );
    }
    return sortDirection === 'asc' ? (
      <svg className="w-4 h-4 text-aalaw-red" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
      </svg>
    ) : (
      <svg className="w-4 h-4 text-aalaw-red" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      </svg>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation user={user} />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <Breadcrumb />

        {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200 sticky top-16 z-40">
        <div className="max-w-full px-6">
          <div className="flex items-center justify-between h-14">
            <div className="flex items-center gap-4">
              <h1 className="text-lg font-semibold text-aalaw-charcoal">Cases</h1>
              <Badge variant="secondary" className="text-xs">
                {totalFiltered.toLocaleString()} {totalFiltered === 1 ? 'case' : 'cases'}
                {totalFiltered !== cases.length && ` of ${cases.length.toLocaleString()} total`}
              </Badge>
              {totalFiltered > 0 && (
                <span className="text-xs text-gray-500">
                  Page {currentPage} of {totalPages}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {/* View Density */}
              <div className="flex items-center gap-1 border border-gray-300 rounded-lg p-1">
                {(['compact', 'comfortable', 'spacious'] as ViewDensity[]).map((density) => (
                  <button
                    key={density}
                    onClick={() => setViewDensity(density)}
                    className={`px-2 py-1 text-xs rounded transition-colors ${
                      viewDensity === density
                        ? 'bg-gray-200 text-gray-900'
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                    title={density.charAt(0).toUpperCase() + density.slice(1)}
                  >
                    {density === 'compact' && '━'}
                    {density === 'comfortable' && '≡'}
                    {density === 'spacious' && '☰'}
                  </button>
                ))}
              </div>
              
              {/* Page Size Selector */}
              <select
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="h-8 px-2 text-xs border border-gray-300 rounded-lg"
              >
                <option value={25}>25 per page</option>
                <option value={50}>50 per page</option>
                <option value={100}>100 per page</option>
                <option value={200}>200 per page</option>
              </select>
              
              <Button variant="outline" size="sm" onClick={handleExport}>
                <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Export
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-full px-6 py-6">
        {/* Filters Bar */}
        <Card className="mb-4">
          <CardContent className="p-4">
            <div className="grid grid-cols-12 gap-3">
              {/* Search */}
              <div className="col-span-3">
                <Input
                  type="text"
                  placeholder="Search cases, clients..."
                  value={searchQuery}
                  onChange={(e) => handleFilterChange(setSearchQuery)(e.target.value)}
                  className="w-full h-9 text-sm"
                />
              </div>

              {/* Status */}
              <div className="col-span-2">
                <select
                  value={statusFilter}
                  onChange={(e) => handleFilterChange(setStatusFilter)(e.target.value)}
                  className="w-full h-9 px-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-aalaw-red focus:border-transparent"
                >
                  <option value="">All Statuses</option>
                  <option value="NEW">New</option>
                  <option value="IN_PROGRESS">In Progress</option>
                  <option value="NEGOTIATION">Negotiation</option>
                  <option value="LITIGATION">Litigation</option>
                  <option value="SETTLED">Settled</option>
                  <option value="CLOSED">Closed</option>
                </select>
              </div>

              {/* Case Type */}
              <div className="col-span-2">
                <select
                  value={caseTypeFilter}
                  onChange={(e) => handleFilterChange(setCaseTypeFilter)(e.target.value)}
                  className="w-full h-9 px-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-aalaw-red focus:border-transparent"
                >
                  <option value="">All Types</option>
                  {caseTypes.map((type) => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>

              {/* Attorney */}
              <div className="col-span-2">
                <select
                  value={attorneyFilter}
                  onChange={(e) => handleFilterChange(setAttorneyFilter)(e.target.value)}
                  className="w-full h-9 px-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-aalaw-red focus:border-transparent"
                >
                  <option value="">All Attorneys</option>
                  {attorneys.map((attorney: any) => (
                    <option key={attorney.id} value={attorney.id}>
                      {attorney.firstName} {attorney.lastName}
                    </option>
                  ))}
                </select>
              </div>

              {/* My Cases Toggle */}
              <div className="col-span-2 flex items-center">
                <label className="flex items-center cursor-pointer text-sm">
                  <input
                    type="checkbox"
                    checked={showOnlyAssigned}
                    onChange={(e) => handleFilterChange(setShowOnlyAssigned)(e.target.checked)}
                    className="w-4 h-4 text-aalaw-red border-gray-300 rounded focus:ring-aalaw-red mr-2"
                  />
                  My Cases Only
                </label>
              </div>

              {/* Clear Filters */}
              <div className="col-span-1 flex items-center justify-end">
                <button
                  onClick={clearFilters}
                  className="text-sm text-gray-600 hover:text-aalaw-red"
                  title="Clear all filters"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Bulk Actions Bar */}
        <BulkActions
          userRole={user?.role}
          resource="cases"
          selectedCount={selectedCases.size}
          onBulkEdit={() => setShowBulkActions(true)}
          onBulkDelete={handleBulkDelete}
          onBulkExport={handleBulkExport}
          onBulkAssign={() => setShowBulkActions(true)}
          className="mb-4"
        />

        {/* Legacy Bulk Assign Modal */}
        {showBulkActions && selectedCases.size > 0 && (
          <Card className="mb-4 bg-blue-50 border-blue-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-4">
                <span className="text-sm font-medium text-blue-900">
                  Assign {selectedCases.size} case{selectedCases.size !== 1 ? 's' : ''} to attorney:
                </span>
                <div className="flex items-center gap-2">
                  <select
                    value={bulkAttorney}
                    onChange={(e) => setBulkAttorney(e.target.value)}
                    className="h-8 px-3 text-sm border border-blue-300 rounded-lg"
                  >
                    <option value="">Select Attorney</option>
                    {attorneys.map((attorney: any) => (
                      <option key={attorney.id} value={attorney.id}>
                        {attorney.firstName} {attorney.lastName}
                      </option>
                    ))}
                  </select>
                  <Button size="sm" onClick={handleBulkAssign} disabled={!bulkAttorney}>
                    Assign
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setShowBulkActions(false)}>
                    Cancel
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Cases Table */}
        <Card>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="w-12 px-4 py-3">
                    <input
                      type="checkbox"
                      checked={selectedCases.size === paginatedCases.length && paginatedCases.length > 0}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                      className="w-4 h-4 text-aalaw-red border-gray-300 rounded focus:ring-aalaw-red"
                    />
                  </th>
                  <th className="text-left px-4 py-3">
                    <button
                      onClick={() => handleSort('caseNumber')}
                      className="flex items-center gap-2 text-xs font-semibold text-gray-700 hover:text-aalaw-red"
                    >
                      Case #
                      <SortIcon field="caseNumber" />
                    </button>
                  </th>
                  <th className="text-left px-4 py-3">
                    <button
                      onClick={() => handleSort('client')}
                      className="flex items-center gap-2 text-xs font-semibold text-gray-700 hover:text-aalaw-red"
                    >
                      Client
                      <SortIcon field="client" />
                    </button>
                  </th>
                  <th className="text-left px-4 py-3">
                    <button
                      onClick={() => handleSort('caseType')}
                      className="flex items-center gap-2 text-xs font-semibold text-gray-700 hover:text-aalaw-red"
                    >
                      Type
                      <SortIcon field="caseType" />
                    </button>
                  </th>
                  <th className="text-left px-4 py-3">
                    <button
                      onClick={() => handleSort('status')}
                      className="flex items-center gap-2 text-xs font-semibold text-gray-700 hover:text-aalaw-red"
                    >
                      Status
                      <SortIcon field="status" />
                    </button>
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-700">
                    Attorney
                  </th>
                  <th className="text-left px-4 py-3">
                    <button
                      onClick={() => handleSort('createdAt')}
                      className="flex items-center gap-2 text-xs font-semibold text-gray-700 hover:text-aalaw-red"
                    >
                      Created
                      <SortIcon field="createdAt" />
                    </button>
                  </th>
                  <th className="text-left px-4 py-3">
                    <button
                      onClick={() => handleSort('updatedAt')}
                      className="flex items-center gap-2 text-xs font-semibold text-gray-700 hover:text-aalaw-red"
                    >
                      Updated
                      <SortIcon field="updatedAt" />
                    </button>
                  </th>
                  <th className="w-24 px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {paginatedCases.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="text-center py-12">
                      <p className="text-gray-500">No cases found</p>
                      <p className="text-gray-400 text-sm mt-1">
                        {searchQuery || statusFilter || caseTypeFilter || attorneyFilter
                          ? 'Try adjusting your filters'
                          : 'Cases will appear here once created'}
                      </p>
                    </td>
                  </tr>
                ) : (
                  paginatedCases.map((caseItem: any) => (
                    <tr
                      key={caseItem.id}
                      className="hover:bg-gray-50 cursor-pointer transition-colors"
                      onClick={() => navigate(`/cases/${caseItem.id}`)}
                    >
                      <td className={`px-4 ${densityClasses[viewDensity]}`} onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selectedCases.has(caseItem.id)}
                          onChange={(e) => handleSelectCase(caseItem.id, e.target.checked)}
                          className="w-4 h-4 text-aalaw-red border-gray-300 rounded focus:ring-aalaw-red"
                        />
                      </td>
                      <td className={`px-4 ${densityClasses[viewDensity]}`}>
                        <span className="text-sm font-mono font-semibold text-aalaw-red">
                          {caseItem.caseNumber}
                        </span>
                      </td>
                      <td className={`px-4 ${densityClasses[viewDensity]}`}>
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {caseItem.client
                              ? `${caseItem.client.firstName} ${caseItem.client.lastName}`
                              : 'No Client'}
                          </div>
                          {caseItem.client?.email && (
                            <div className="text-xs text-gray-500">{caseItem.client.email}</div>
                          )}
                        </div>
                      </td>
                      <td className={`px-4 ${densityClasses[viewDensity]}`}>
                        <span className="text-sm text-gray-700">{caseItem.caseType}</span>
                      </td>
                      <td className={`px-4 ${densityClasses[viewDensity]}`}>
                        <Badge variant={getStatusBadgeVariant(caseItem.status)} className="text-xs">
                          {caseItem.status.replace('_', ' ')}
                        </Badge>
                      </td>
                      <td className={`px-4 ${densityClasses[viewDensity]}`}>
                        <span className="text-sm text-gray-700">
                          {getStaffName(caseItem.assignedAttorneyId)}
                        </span>
                      </td>
                      <td className={`px-4 ${densityClasses[viewDensity]}`}>
                        <span className="text-sm text-gray-600">
                          {format(new Date(caseItem.createdAt), 'MMM d, yyyy')}
                        </span>
                      </td>
                      <td className={`px-4 ${densityClasses[viewDensity]}`}>
                        <span className="text-sm text-gray-600">
                          {format(new Date(caseItem.updatedAt), 'MMM d, yyyy')}
                        </span>
                      </td>
                      <td className={`px-4 ${densityClasses[viewDensity]}`} onClick={(e) => e.stopPropagation()}>
                        <CaseManagementActions
                          caseId={caseItem.id}
                          caseNumber={caseItem.caseNumber}
                          userRole={user?.role || ''}
                          onView={() => navigate(`/cases/${caseItem.id}`)}
                          onEdit={() => handleEditCase(caseItem)}
                          onDelete={() => handleDeleteCase(caseItem.id)}
                          onRefresh={refetch}
                        />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="p-4 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-600">
                  Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalFiltered)} of {totalFiltered.toLocaleString()} cases
                </div>
                
                <div className="flex items-center gap-2">
                  {/* Previous Button */}
                  <button
                    onClick={() => goToPage(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="px-3 py-1 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  
                  {/* Page Numbers */}
                  <div className="flex items-center gap-1">
                    {getPageNumbers().map((page, index) => (
                      typeof page === 'string' ? (
                        <span key={`ellipsis-${index}`} className="px-2 text-gray-500">{page}</span>
                      ) : (
                        <button
                          key={`page-${page}`}
                          onClick={() => goToPage(page)}
                          className={`px-3 py-1 text-sm rounded-lg transition-colors ${
                            currentPage === page
                              ? 'bg-aalaw-red text-white font-semibold'
                              : 'border border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          {page}
                        </button>
                      )
                    ))}
                  </div>
                  
                  {/* Next Button */}
                  <button
                    onClick={() => goToPage(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          )}
        </Card>
      </main>
      </div>

      {/* Edit Case Modal */}
      {editingCase && (
        <EditCaseModal
          caseData={editingCase}
          onClose={() => setEditingCase(null)}
          onSuccess={refetch}
        />
      )}
    </div>
  );
}
